import Vue from 'vue';

const NOTIFY_COLORS = {
    primary: '#1f74ff',
    success: '#46c93a',
    danger: '#ff4757',
    warning: '#ffba00',
    dark: '#1e1e1e'
};

function getContainer(position = 'bottom-center') {
    let container = document.getElementById(`hn-notify-${position}`);
    if (!container) {
        container = document.createElement('div');
        container.id = `hn-notify-${position}`;
        container.className = `hn-notifications hn-${position}`;
        document.body.appendChild(container);
    }
    return container;
}

const ICON_MAP = {
    check_box: 'mdi-checkbox-marked-circle-outline',
    check: 'mdi-check-circle-outline',
    done: 'mdi-check-circle-outline',
    warning: 'mdi-alert-circle-outline',
    error: 'mdi-alert-octagon-outline',
    close: 'mdi-close-circle-outline',
    cancel: 'mdi-close-circle-outline',
    content_copy: 'mdi-content-copy',
    lock: 'mdi-lock-outline',
    delete: 'mdi-delete-outline',
    info: 'mdi-information-outline',
};

function resolveIconClass(icon) {
    if (!icon) return '';
    if (ICON_MAP[icon]) {
        return `mdi ${ICON_MAP[icon]}`;
    }
    if (icon.startsWith('mdi-')) {
        return `mdi ${icon}`;
    }
    if (icon.startsWith('mdi ')) {
        return icon;
    }
    return `mdi mdi-${icon}`;
}

export function notify(options = {}) {
    if (typeof document === 'undefined') return;

    const {
        title = '',
        text = '',
        color = 'primary',
        icon = '',
        position = 'bottom-center',
        time = 4000,
        click = null
    } = options;

    const container = getContainer(position);

    const noti = document.createElement('div');
    noti.className = 'hn-noti';
    
    // Resolver color de fondo
    const bgColor = NOTIFY_COLORS[color] || (color.startsWith('#') ? color : NOTIFY_COLORS.primary);
    noti.style.backgroundColor = bgColor;

    // Contenido
    let html = '';
    if (icon) {
        html += `<i class="hn-noti-icon ${resolveIconClass(icon)}"></i>`;
    }
    html += '<div class="hn-noti-content">';
    if (title) {
        html += `<h4 class="hn-noti-title">${title}</h4>`;
    }
    if (text) {
        html += `<p class="hn-noti-text">${text}</p>`;
    }
    html += '</div>';
    
    noti.innerHTML = html;

    // Descarte
    let isDismissed = false;
    const dismiss = () => {
        if (isDismissed) return;
        isDismissed = true;
        noti.classList.remove('hn-noti-active');
        noti.classList.add('hn-noti-leaving');
        setTimeout(() => {
            if (noti.parentNode) {
                noti.parentNode.removeChild(noti);
            }
        }, 300);
    };

    noti.addEventListener('click', (e) => {
        if (typeof click === 'function') click(e);
        dismiss();
    });

    container.appendChild(noti);

    // Animación de entrada
    requestAnimationFrame(() => {
        noti.classList.add('hn-noti-active');
    });

    if (time && time > 0) {
        setTimeout(dismiss, time);
    }

    return { close: dismiss };
}

// Inyectar estilos CSS una sola vez
if (typeof document !== 'undefined') {
    const styleId = 'hn-notifications-style';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            .hn-notifications {
                position: fixed;
                z-index: 100000;
                display: flex;
                flex-direction: column;
                gap: 10px;
                pointer-events: none;
                max-width: 90vw;
                width: 400px;
                box-sizing: border-box;
            }
            .hn-bottom-center {
                bottom: 24px;
                left: 50%;
                transform: translateX(-50%);
                align-items: center;
            }
            .hn-top-center {
                top: 24px;
                left: 50%;
                transform: translateX(-50%);
                align-items: center;
            }
            .hn-top-right {
                top: 24px;
                right: 24px;
                align-items: flex-end;
            }
            .hn-bottom-right {
                bottom: 24px;
                right: 24px;
                align-items: flex-end;
            }
            .hn-top-left {
                top: 24px;
                left: 24px;
                align-items: flex-start;
            }
            .hn-bottom-left {
                bottom: 24px;
                left: 24px;
                align-items: flex-start;
            }
            .hn-noti {
                pointer-events: auto;
                cursor: pointer;
                color: #ffffff;
                border-radius: 14px;
                padding: 12px 18px;
                box-shadow: 0px 10px 25px -5px rgba(0, 0, 0, 0.25);
                display: flex;
                align-items: center;
                gap: 12px;
                font-family: Rubik, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                min-width: 260px;
                max-width: 100%;
                opacity: 0;
                transform: translateY(20px) scale(0.95);
                transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.2), opacity 0.25s ease;
                box-sizing: border-box;
                user-select: none;
            }
            .hn-top-center .hn-noti,
            .hn-top-right .hn-noti,
            .hn-top-left .hn-noti {
                transform: translateY(-20px) scale(0.95);
            }
            .hn-noti.hn-noti-active {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
            .hn-noti.hn-noti-leaving {
                opacity: 0;
                transform: scale(0.85);
            }
            .hn-noti-icon {
                font-size: 24px !important;
                line-height: 1 !important;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }
            .hn-noti-content {
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1;
                text-align: left;
            }
            .hn-noti-title {
                margin: 0;
                font-size: 0.92rem;
                font-weight: 700;
                line-height: 1.25;
                color: inherit;
            }
            .hn-noti-text {
                margin: 2px 0 0 0;
                font-size: 0.82rem;
                font-weight: 400;
                line-height: 1.3;
                opacity: 0.92;
                color: inherit;
            }
        `;
        document.head.appendChild(style);
    }
}

const NotifyPlugin = {
    install(VueInstance) {
        if (!VueInstance.prototype.$vs) {
            VueInstance.prototype.$vs = {};
        }
        VueInstance.prototype.$vs.notify = notify;
    }
};

Vue.use(NotifyPlugin);

export default NotifyPlugin;
